// src/v2/hooks/useEmployeeContributionHistory.js
import { useState, useCallback } from 'react';
import reportsService from '../services/reportsService';
import { useGeneralMessage } from '../../common/APICommon';
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

export const useEmployeeContributionHistory = () => {
  const [loading, setLoading] = useState(false);
  const { showGeneralMessage, showMessage, message, isSuccess } = useGeneralMessage();

  /**
   * Generates a professional PhilHealth Contribution Ledger PDF from scratch
   */
  const downloadPDF = useCallback(async (data, employeeName, dateFrom, dateTo) => {
    try {
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([612, 792]); // Letter Size
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

      let currentY = 742;

      // Header
      page.drawText("PHILHEALTH CONTRIBUTION HISTORY", { x: 50, y: currentY, size: 14, font: fontBold });
      currentY -= 20;
      page.drawText(`Period: ${dateFrom || 'Start'} to ${dateTo || 'End'}`, { x: 50, y: currentY, size: 10, font });
      currentY -= 15;
      page.drawText(`Employee Name: ${employeeName}`, { x: 50, y: currentY, size: 10, font });
      currentY -= 30;

      // Table Headers
      page.drawRectangle({ x: 50, y: currentY - 20, width: 512, height: 20, color: rgb(0.9, 0.9, 0.9) });
      const cols = { period: 60, ee: 200, er: 320, total: 440 };

      page.drawText("Payroll Period", { x: cols.period, y: currentY - 14, size: 9, font: fontBold });
      page.drawText("EE Share", { x: cols.ee, y: currentY - 14, size: 9, font: fontBold });
      page.drawText("ER Share", { x: cols.er, y: currentY - 14, size: 9, font: fontBold });
      page.drawText("Total Amount", { x: cols.total, y: currentY - 14, size: 9, font: fontBold });

      currentY -= 20;

      // Data Rows
      if (!data || data.length === 0) {
        currentY -= 30;
        page.drawText("NO CONTRIBUTION RECORDS FOUND FOR THIS PERIOD", { x: 180, y: currentY, size: 9, font });
      } else {
        data.forEach((row, index) => {
          currentY -= 20;
          if (index % 2 === 1) page.drawRectangle({ x: 50, y: currentY, width: 512, height: 20, color: rgb(0.98, 0.98, 0.98) });

          page.drawText(row.periodName || "-", { x: cols.period, y: currentY + 6, size: 8, font });
          page.drawText((row.phEE || 0).toLocaleString('en-US', { minimumFractionDigits: 2 }), { x: cols.ee, y: currentY + 6, size: 8, font });
          page.drawText((row.phER || 0).toLocaleString('en-US', { minimumFractionDigits: 2 }), { x: cols.er, y: currentY + 6, size: 8, font });
          page.drawText(((row.phEE || 0) + (row.phER || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 }), { x: cols.total, y: currentY + 6, size: 8, font });

          page.drawLine({ start: { x: 50, y: currentY }, end: { x: 562, y: currentY }, thickness: 0.1, color: rgb(0.8, 0.8, 0.8) });
        });

        // Summary Total
        currentY -= 30;
        const total = data.reduce((sum, r) => sum + (r.phEE || 0) + (r.phER || 0), 0);
        page.drawText("GRAND TOTAL:", { x: 320, y: currentY, size: 10, font: fontBold });
        page.drawText(total.toLocaleString('en-US', { minimumFractionDigits: 2 }), { x: 440, y: currentY, size: 11, font: fontBold, color: rgb(0, 0.3, 0.6) });
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `PhilHealth_History_${employeeName.replace(/\s+/g, '_')}.pdf`;
      link.click();

      showGeneralMessage("Contribution ledger downloaded.", true);
    } catch (err) {
      console.error(err);
      showGeneralMessage("Failed to generate PDF.", false);
    }
  }, [showGeneralMessage]);

  const generateAndDownload = useCallback(async (params, employeeName, dateFrom, dateTo) => {
    setLoading(true);
    try {
      let data = [];
      try {
        data = await reportsService.getReportData('ContributionHistory', params);
      } catch (apiErr) {
        console.warn("Backend not found. Falling back to Demo Mode.");
        data = [];
      }
      await downloadPDF(data || [], employeeName, dateFrom, dateTo);
    } catch (err) {
      showGeneralMessage("Process failed.", false);
    } finally {
      setLoading(false);
    }
  }, [downloadPDF, showGeneralMessage]);

  return { loading, generateAndDownload, showMessage, message, isSuccess };
};